import { useCallback, useState, useEffect, useRef } from "react"
import { useDropzone } from "react-dropzone"
import styled from "styled-components"
import ArchiveUploaderContent from "./ArchiveUploaderContent"
import MainButton from "../../components/buttons"
import JSZip from "jszip"
import fetchApi from "../../utils/fetchApi"

const ZIP_MIME_TYPES = ["application/zip", "application/octet-stream", "application/x-zip-compressed", "multipart/x-zip"]

export default function ArchiveUploader({
  taskId,
  contestId,
  limits,
  submitCallback,
}) {
  const [state, setState] = useState("idle")
  const [loadedInfo, setLoadedInfo] = useState({})

  const currentFile = useRef(null)

  const readError = useCallback((file, error) => {
      setLoadedInfo({
        filename: file[0].name,
        size: file[0].size,
        count: 1,
        errorDetails: error,
      })
      setState("idle")
  }, [])

  const upload = useCallback(file => {
    setState("loading")

    const reader = new FileReader();

    reader.onabort = () => readError(file, "Произошла ошибка при чтении файла")
    reader.onerror = () => readError(file, "Произошла ошибка при чтении файла")
    reader.onload = async () => {
      if (!ZIP_MIME_TYPES.includes(file[0].type)) {
        readError(file, "Файл не является ZIP-архивом")
        return
      }
      try {
        const zip = new JSZip()
        await zip.loadAsync(reader.result)
        setLoadedInfo({
          filename: file[0].name,
          size: file[0].size,
          count: Object.keys(zip.files).length,
          errorDetails: null,
        })
        currentFile.current = file[0]
        setState("idle")
      } catch (e) {
        readError(file, "Не удалось прочитать ZIP-архив")
      }
    }
    try {
      reader.readAsArrayBuffer(file[0])
    } catch (e) {
      setLoadedInfo({
        filename: "неизвестно",
        size: 0,
        count: 0,
        errorDetails: "Произошла ошибка при чтении файла",
      })
      setState("idle")
    }
  }, [readError])

  const send = useCallback(async () => {
    let formData = new FormData();
    formData.append("document", currentFile.current);

    const apiResponse = await fetchApi({
      url: "/submit_zip",
      method: "post",
      data: formData,
      params: {
        "task_id": taskId,
        "contest_id": contestId,
      },
      headers: { "Content-Type": "multipart/form-data" },
    }).catch(error => ({ error: error.response?.data?.error ?? "Ошибка при чтении файла на сервере" }))

    if (apiResponse.error) {
      setLoadedInfo(info => ({
        ...info,
        errorDetails: apiResponse.error,
      }))
      setState("idle")
      return
    }

    setState("idle")

    if (apiResponse.id) setLoadedInfo({})
    submitCallback(apiResponse.id)

    return apiResponse
  }, [contestId, submitCallback, taskId])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop: upload, multiple: false })

  useEffect(() => {
    // only toggles the hover look: must not reset "loading" when the drop ends
    setState(current => isDragActive ? "hover" : current === "hover" ? "idle" : current)
  }, [isDragActive])

  return (
    <>
      <Uploader {...getRootProps()}>
        <ArchiveUploaderContent
          state={state}
          loadedInfo={loadedInfo}
          taskId={taskId}
          contestId={contestId}
        />
        <input {...getInputProps({
          accept: "application/zip",
        })} />
      </Uploader>
      {/* same layout as the editor's send row (Tasks.css .task-send-block) */}
      <div className="task-send-block">
        <MainButton
          id="send-task"
          onClick={send}
          disabled={!loadedInfo.filename || !!loadedInfo.errorDetails}
        >Отправить</MainButton>
        <div className="task-limits">
          <p>Лимит времени: {limits.time} мс</p>
          <p>Лимит памяти: {limits.memory} мб</p>
        </div>
      </div>
    </>
  )
}

const Uploader = styled.div`
  cursor: pointer;
  background-color: var(--body-background);
  border: 1px solid var(--code-editor-outline);
  border-radius: 4px;
  transition: border-color var(--transition-timing) var(--transition-function),
    background-color var(--transition-timing) var(--transition-function);

  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-height: 204px;
  padding: 0 16px;
`
